import type { Node } from '@/data-explorer-v2/entities/node';
import { dynamicAmpereUnit } from '@/utils/dynamic-current-unit';
import { dynamicPowerUnit } from '@/utils/dynamic-power-unit';
import { dynamicVoltageUnit } from '@/utils/dynamic-voltage-unit';
import {
  formatAmpereWithUnit,
  formatEnergy,
  formatFrequency,
  formatPower,
  formatPowerFactor,
  formatVoltageWithUnit,
} from '@/utils/format/battery';
import { formatNumber, formatPercentage } from '@/utils/format/number';
import { formatTemperature } from '@/utils/format/temperature';

import { ChartType } from '../../entities/chart-type';

export const chartTypeLabel: Record<ChartType, string> = {
  [ChartType.VOLTAGE]: 'Voltage',
  [ChartType.CURRENT]: 'Current',
  [ChartType.VOLTAGE_AC]: 'Voltage AC',
  [ChartType.CURRENT_AC]: 'Current AC',
  [ChartType.SOC]: 'State of Charge',
  [ChartType.TEMPERATURE]: 'Temperature',
  [ChartType.CELL_VOLTAGE]: 'Cell Voltage',
  [ChartType.POWER]: 'Power',
  [ChartType.POWER_FACTOR]: 'Power Factor',
  [ChartType.ENERGY]: 'Energy',
  [ChartType.FREQUENCY]: 'Frequency in Hz',
  [ChartType.UNITS_ONLINE]: 'Units Online',
  [ChartType.RACKS_ONLINE]: 'Racks Online',
  [ChartType.HS_CONTROL_AGC]: 'HS Control AGC',
};

export const chartTypeFormatter: Record<ChartType, (value: number) => string> =
  {
    [ChartType.VOLTAGE]: (value) =>
      formatVoltageWithUnit(value, dynamicVoltageUnit(value), 1, 2),
    [ChartType.CURRENT]: (value) =>
      formatAmpereWithUnit(value, dynamicAmpereUnit(value), 1, 2),
    [ChartType.VOLTAGE_AC]: (value) =>
      formatVoltageWithUnit(value, dynamicVoltageUnit(value), 1, 2),
    [ChartType.CURRENT_AC]: (value) =>
      formatAmpereWithUnit(value, dynamicAmpereUnit(value), 1, 2),
    [ChartType.SOC]: (value) => formatPercentage(value / 100, 1, 2),
    [ChartType.TEMPERATURE]: (value) => formatTemperature(value, 1, 2),
    [ChartType.CELL_VOLTAGE]: (value) =>
      formatVoltageWithUnit(value, dynamicVoltageUnit(value), 1, 2),
    [ChartType.POWER]: (value) =>
      formatPower(value, dynamicPowerUnit(value), 1, 2),
    [ChartType.POWER_FACTOR]: (value) => formatPowerFactor(value, 1, 2),
    [ChartType.ENERGY]: (value) =>
      formatEnergy(value, dynamicPowerUnit(value), 1, 2),
    [ChartType.FREQUENCY]: (value) => formatFrequency(value, 1, 2),
    [ChartType.UNITS_ONLINE]: (value) => formatNumber(value, 0, 0),
    [ChartType.RACKS_ONLINE]: (value) => formatNumber(value, 0, 0),
    [ChartType.HS_CONTROL_AGC]: (value) =>
      formatPower(value, dynamicPowerUnit(value), 1, 2),
  };

export function nameFromNode(
  node: Node,
  column?: string,
  hasMultipleNodes = false,
): string {
  // Helper function to format column names with aggregation info
  const formatColumnName = (columnName: string): string => {
    if (columnName.endsWith('_avg')) {
      const baseName = columnName.slice(0, -4); // Remove '_avg'
      return `${baseName} (avg)`;
    } else if (columnName.endsWith('_min')) {
      const baseName = columnName.slice(0, -4); // Remove '_min'
      return `${baseName} (min)`;
    } else if (columnName.endsWith('_max')) {
      const baseName = columnName.slice(0, -4); // Remove '_max'
      return `${baseName} (max)`;
    }

    // If no aggregation suffix, return the column name directly
    return columnName;
  };

  // Helper function to create a readable node identifier
  const getNodeIdentifier = (node: Node): string => {
    // Use the node label, but clean it up for better readability
    let identifier = node.label;

    // Clean up common patterns
    identifier = identifier.replace(/battery_/gi, '').replace(/_/g, ' ');

    // If still very long, use smarter truncation that preserves the end
    // (where node IDs usually differ)
    if (identifier.length > 50) {
      const start = identifier.substring(0, 25);
      const end = identifier.substring(identifier.length - 20);
      identifier = `${start}...${end}`;
    }

    return identifier;
  };

  if (column && hasMultipleNodes) {
    // For multi-node scenarios: "NodeId - column (aggregation)"
    return `${getNodeIdentifier(node)} - ${formatColumnName(column)}`;
  } else if (column && !hasMultipleNodes) {
    // For single-node scenarios: keep clean naming with just column info
    return formatColumnName(column);
  } else {
    // Fallback for loading states or when no column provided
    return hasMultipleNodes ? getNodeIdentifier(node) : node.label;
  }
}
